import React, { useState, useEffect, useCallback, useRef } from 'react'
import Spiner from '../../components/modals/Spiner'
import productsLogic from '../../functions/logic/productsLogic'
import ProductsGrid from '../../components/ProductsGrid'
import '../../scss/producSear.scss'

export default function ProductsSearch() {
  const [currentPage, setCurrentPage] = useState(1)
  const [products, setProducts] = useState([])
  const [totalPages, setTotalPages] = useState(1)
  const [load, setLoad] = useState(false)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [priceRange, setPriceRange] = useState([0, 1000])

  const [catalogoFilter, setCatalogo] = useState([])
  const [marcaFilter, setMarca] = useState([])

  const [filters, setFilters] = useState({
    category: [],
    brand: []
  })

  const [searchTerm, setSearchTerm] = useState('')

  const requestIdRef = useRef(0)
  const searchTimeoutRef = useRef(null)

  const getProducts = useCallback(async () => {
    const requestId = ++requestIdRef.current

    try {
      setLoad(true)

      const response = await productsLogic.getProductsSearchLogic(
        currentPage,
        global.PEROD_BY_PAGE,
        searchTerm.trim() || 'no',
        filters,
        priceRange
      )

      if (requestId !== requestIdRef.current) {
        return
      }

      if (response.success && response.data && response.data.length > 0) {
        setProducts(response.data)

        const total = Number(response.data[0].total) || 0

        setTotalPages(Math.max(1, Math.ceil(total / global.PEROD_BY_PAGE)))
      } else {
        setProducts([])
        setTotalPages(1)
      }
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return
      }

      setProducts([])
      setTotalPages(1)
    } finally {
      if (requestId === requestIdRef.current) {
        setLoad(false)
      }
    }
  }, [currentPage, filters, priceRange, searchTerm])

  useEffect(() => {
    getFilters()
  }, [])

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      getProducts()
    }, 350)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [getProducts])

  const getFilters = async () => {
    try {
      setLoad(true)

      const response = await productsLogic.getFiltersLogic()

      if (response.success) {
        setCatalogo(response.data?.categorias || [])
        setMarca(response.data?.marcas || [])
      } else {
        setCatalogo([])
        setMarca([])
      }
    } catch (error) {
      setCatalogo([])
      setMarca([])
    } finally {
      setLoad(false)
    }
  }

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleSearchSubmit = (event) => {
    event.preventDefault()

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    setCurrentPage(1)
    getProducts()
  }

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1)

    setFilters((prevFilters) => {
      const currentValues = prevFilters[filterType] || []

      const exists = currentValues.includes(value)

      return {
        ...prevFilters,
        [filterType]: exists
          ? currentValues.filter((item) => item !== value)
          : [...currentValues, value]
      }
    })
  }

  const clearFilters = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    setCurrentPage(1)

    setFilters({
      category: [],
      brand: []
    })

    setSearchTerm('')
    setPriceRange([0, 1000])
  }

  const paginate = (event, pageNumber) => {
    setCurrentPage(pageNumber)

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const selectedCategoryCount = filters.category.length
  const selectedBrandCount = filters.brand.length

  const totalFilters =
    selectedCategoryCount +
    selectedBrandCount +
    (searchTerm.trim() !== '' ? 1 : 0)

  return (
    <main className="main">
      <Spiner opt={load} />

      {/* FILTROS MOBILE */}
      <div className="mobile-filters-bar">
        <button
          type="button"
          className="mobile-filters-button"
          onClick={() => setIsMobileFiltersOpen(true)}
        >
          <i className="icon-sliders"></i>
          <span>Filtros</span>

          {totalFilters > 0 && (
            <span className="mobile-filter-count">{totalFilters}</span>
          )}
        </button>
      </div>

      {/* OVERLAY FILTROS MOBILE */}
      {isMobileFiltersOpen && (
        <div
          className="mobile-filters-overlay"
          onClick={() => setIsMobileFiltersOpen(false)}
        />
      )}

      <div className="page-content">
        <div className="col-lg-12">
          <div className="row summary">
            <div className="col-lg-10">
              <ProductsGrid
                data={products}
                totalPages={totalPages}
                paginate={paginate}
                currentPage={currentPage}
              />
            </div>

            <aside
              className={`col-lg-2 order-lg-first mobile-filters-sidebar ${
                isMobileFiltersOpen ? 'mobile-filters-open' : ''
              }`}
            >
              <div className="sidebar sidebar-shop">
                <div className="mobile-filters-header">
                  <h3>Filtros</h3>

                  <button
                    type="button"
                    onClick={() => setIsMobileFiltersOpen(false)}
                    aria-label="Cerrar filtros"
                  >
                    <i className="icon-close"></i>
                  </button>
                </div>
                {/* FILTROS */}
                <div className="widget widget-clean">
                  <div className="filter-header">
                    <label>
                      Filtros
                      {totalFilters > 0 && (
                        <span className="filter-count">{totalFilters}</span>
                      )}
                    </label>

                    <button
                      type="button"
                      className="sidebar-filter-clear"
                      onClick={clearFilters}
                    >
                      Limpiar filtros
                    </button>
                  </div>
                </div>

                {/* BUSCAR */}
                <div className="widget widget-collapsible">
                  <h3 className="widget-title">
                    <a
                      data-toggle="collapse"
                      href="#widget-search"
                      role="button"
                      aria-expanded="true"
                      aria-controls="widget-search"
                    >
                      Buscar
                    </a>
                  </h3>

                  <div className="collapse show" id="widget-search">
                    <div className="widget-body">
                      <form
                        className="products-search-form"
                        onSubmit={handleSearchSubmit}
                      >
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            name="title"
                            placeholder="Buscar por nombre"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            autoComplete="off"
                          />

                          {searchTerm && (
                            <button
                              type="button"
                              className="search-clear"
                              onClick={() => {
                                setSearchTerm('')
                                setCurrentPage(1)
                              }}
                              aria-label="Limpiar búsqueda"
                            >
                              ×
                            </button>
                          )}

                          <button
                            type="submit"
                            className="btn-search input-group-text"
                            aria-label="Buscar"
                          >
                            <i className="icon-search"></i>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>

                {/* CATEGORÍAS */}
                <div className="widget widget-collapsible">
                  <h3 className="widget-title">
                    <a
                      data-toggle="collapse"
                      href="#widget-1"
                      role="button"
                      aria-expanded="true"
                      aria-controls="widget-1"
                    >
                      <span>
                        Categoría
                        {selectedCategoryCount > 0 && (
                          <span className="filter-count">
                            {selectedCategoryCount}
                          </span>
                        )}
                      </span>
                    </a>
                  </h3>

                  <div className="collapse show" id="widget-1">
                    <div className="widget-body">
                      <div className="filter-items filter-items-count">
                        {catalogoFilter.map(
                          ({ id, nombre, count, nemonico_cat }) => (
                            <div className="filter-item" key={id}>
                              <div className="custom-control custom-checkbox">
                                <input
                                  type="checkbox"
                                  className="custom-control-input"
                                  id={`category-${nemonico_cat}`}
                                  checked={filters.category.includes(
                                    nemonico_cat
                                  )}
                                  onChange={() =>
                                    handleFilterChange('category', nemonico_cat)
                                  }
                                />

                                <label
                                  className="custom-control-label"
                                  htmlFor={`category-${nemonico_cat}`}
                                >
                                  <span>{nombre}</span>

                                  {count !== undefined && (
                                    <span className="filter-item-count">
                                      {count}
                                    </span>
                                  )}
                                </label>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* MARCAS */}
                <div className="widget widget-collapsible">
                  <h3 className="widget-title">
                    <a
                      data-toggle="collapse"
                      href="#widget-4"
                      role="button"
                      aria-expanded="true"
                      aria-controls="widget-4"
                    >
                      <span>
                        Marca
                        {selectedBrandCount > 0 && (
                          <span className="filter-count">
                            {selectedBrandCount}
                          </span>
                        )}
                      </span>
                    </a>
                  </h3>

                  <div className="collapse show" id="widget-4">
                    <div className="widget-body">
                      <div className="filter-items">
                        {marcaFilter.map(({ id, nombre, nemonico_marca }) => (
                          <div className="filter-item" key={id}>
                            <div className="custom-control custom-checkbox">
                              <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`brand-${nemonico_marca}`}
                                checked={filters.brand.includes(nemonico_marca)}
                                onChange={() =>
                                  handleFilterChange('brand', nemonico_marca)
                                }
                              />

                              <label
                                className="custom-control-label"
                                htmlFor={`brand-${nemonico_marca}`}
                              >
                                <span>{nombre}</span>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </main>
  )
}
